import {
	Dialog as AriaDialog,
	type DialogProps as AriaDialogProps,
	DialogTrigger as AriaDialogTrigger,
	Heading,
	Modal,
	ModalOverlay,
	type ModalOverlayProps,
} from 'react-aria-components'
import { Button } from '../button/button'
import { cn } from '../lib/cn'

const SIZE = {
	sm: 'max-w-sm',
	md: 'max-w-md',
	lg: 'max-w-lg',
	xl: 'max-w-xl',
	full: 'w-fit max-w-full',
} as const

/**
 * Props for the {@link Dialog} component.
 *
 * @property children - Trigger element and dialog content
 *
 * @example
 * ```tsx
 * <Dialog>
 *   <DialogTriggerButton>Open</DialogTriggerButton>
 *   <DialogContent>{({ close }) => <Button onPress={close}>Done</Button>}</DialogContent>
 * </Dialog>
 * ```
 */
export interface DialogProps {
	children: React.ReactNode
}

/**
 * Props for the {@link DialogContent} component.
 *
 * @property size - Panel width step (`sm` default, `md`, `lg`, `xl`)
 * @property role - `dialog` (default) or `alertdialog`, forwarded to the panel
 * @property className - Optional additional Tailwind classes for the `role="dialog"` panel
 * @property overlayClassName - Optional additional Tailwind classes for the scrim behind it
 * @property children - Dialog content; a function child receives `{ close }`
 */
export interface DialogContentProps
	extends Omit<ModalOverlayProps, 'className' | 'children' | 'aria-label' | 'aria-labelledby' | 'aria-describedby'> {
	size?: keyof typeof SIZE
	role?: AriaDialogProps['role']
	className?: string
	overlayClassName?: string
	children: AriaDialogProps['children']
	'aria-label'?: string
	'aria-labelledby'?: string
	'aria-describedby'?: string
}

/**
 * Accessible modal dialog primitive using React Aria Components.
 *
 * @remarks
 * - **Accessibility:** Focus containment, autofocus, focus restore to the trigger,
 *   and page isolation (everything outside the modal is hidden from assistive
 *   technology) provided by React Aria's `<ModalOverlay>`/`<Modal>`/`<Dialog>`.
 * - **Styling:** All colors and spacing use design tokens from `@atlas/tokens`.
 *   `className` targets the `role="dialog"` panel; `overlayClassName` the scrim.
 * - **Control:** Controlled-first — pass `isOpen`/`onOpenChange`; also works
 *   uncontrolled inside a {@link Dialog} trigger.
 * - **Dismissal:** Esc closes by default (`isKeyboardDismissDisabled` opts out,
 *   e.g. while a mutation runs); the scrim is inert unless `isDismissable`.
 * - **Labelling:** Use {@link DialogTitle} for the accessible name; `aria-label`,
 *   `aria-labelledby`, and `aria-describedby` are forwarded to the panel element.
 * - **Portal:** Renders into `document.body`, above the page (`z-50` scrim).
 *
 * @example Controlled confirm-shaped dialog
 * ```tsx
 * <DialogContent isOpen={open} onOpenChange={(next) => { if (!next) onCancel() }}>
 *   <DialogTitle>Delete file?</DialogTitle>
 *   <p>This cannot be undone.</p>
 * </DialogContent>
 * ```
 *
 * @see packages/ui-primitives/src/dialog/dialog.stories.tsx for visual examples
 */
export function DialogContent({
	size = 'sm',
	role,
	className,
	overlayClassName,
	children,
	'aria-label': ariaLabel,
	'aria-labelledby': ariaLabelledby,
	'aria-describedby': ariaDescribedby,
	...props
}: DialogContentProps) {
	return (
		<ModalOverlay
			{...props}
			className={cn('fixed inset-0 z-50 flex items-center justify-center bg-ds-text/40 p-4', overlayClassName)}
		>
			<Modal className={cn('flex max-h-full w-full flex-col', SIZE[size])}>
				<AriaDialog
					role={role}
					aria-label={ariaLabel}
					aria-labelledby={ariaLabelledby}
					aria-describedby={ariaDescribedby}
					className={cn('rounded-ds-modal bg-ds-surface-elevated p-6 shadow-ds-xl outline-none', className)}
				>
					{children}
				</AriaDialog>
			</Modal>
		</ModalOverlay>
	)
}

/**
 * Props for the {@link DialogTitle} component.
 *
 * @property className - Optional additional Tailwind classes
 * @property children - The title text
 */
export interface DialogTitleProps {
	className?: string
	children: React.ReactNode
}

/**
 * Dialog title (must be used inside {@link DialogContent}).
 *
 * @remarks
 * Renders an `h2` that React Aria wires to the dialog's `aria-labelledby`,
 * so the dialog is named without hand-managed ids.
 */
export function DialogTitle({ className, children }: DialogTitleProps) {
	return (
		<Heading slot="title" className={cn('font-ds-display text-ds-xl leading-7 font-semibold text-ds-text', className)}>
			{children}
		</Heading>
	)
}

/**
 * Dialog trigger wrapper for uncontrolled use (pairs a pressable trigger with
 * {@link DialogContent}), mirroring the Popover/Menu primitives.
 *
 * @example
 * ```tsx
 * <Dialog>
 *   <DialogTriggerButton variant="outline">Open</DialogTriggerButton>
 *   <DialogContent>
 *     <DialogTitle>Hello</DialogTitle>
 *   </DialogContent>
 * </Dialog>
 * ```
 */
export function Dialog({ children }: DialogProps) {
	return <AriaDialogTrigger>{children}</AriaDialogTrigger>
}

// Re-export Button for convenience
export { Button as DialogTriggerButton }
